export type LiveCategoryId =
  | 'baccarat'
  | 'blackjack'
  | 'roulette'
  | 'teenpatti'
  | 'poker'
  | 'table'
  | 'live'
  | 'gameShow'

export type LiveCategoryDef = {
  id: LiveCategoryId
  label: string
  filterLabel: string
}

export const LIVE_CATEGORY_ORDER: LiveCategoryDef[] = [
  { id: 'baccarat', label: 'BACCARAT', filterLabel: 'Baccarat' },
  { id: 'blackjack', label: 'BLACKJACK', filterLabel: 'Blackjack' },
  { id: 'roulette', label: 'ROULETTE', filterLabel: 'Roulette' },
  { id: 'teenpatti', label: 'TEEN PATTI', filterLabel: 'Teen Patti' },
  { id: 'poker', label: 'POKER', filterLabel: 'Poker' },
  { id: 'table', label: 'TABLE GAMES', filterLabel: 'Table Games' },
  { id: 'live', label: 'LIVE GAMES', filterLabel: 'Live Games' },
  { id: 'gameShow', label: 'GAME SHOWS', filterLabel: 'Game Shows' },
]

const LIVE_PROVIDERS = new Set([
  'Evolution Live',
  'Ezugi',
  'Playtech',
  'KM',
  'KingMidas',
  'King Midas',
  'Microgaming',
  'KoolBet',
  'Mini',
  '2J',
  'V8',
  'Turbogames',
  'Mac88',
  'Mac88 Gaming',
])

const SLOT_GROUP_RE = /^(slot game|video slots|slots|slot|pop slots|classic games|arcade)$/i

const FALSE_POSITIVE_NAMES = [
  /dragon\s?tiger\s?luck/i,
  /fire\s?lightning/i,
  /platinum\s?lightning/i,
  /lightning\s?bomb/i,
  /baccarat\s?deluxe/i,
  /lightning\s?leopard/i,
  /lightning\s?joker/i,
  /lightning\s?shenlong/i,
]

const CATEGORY_PATTERNS: Record<LiveCategoryId, RegExp> = {
  baccarat: /baccarat|bac\s?bo|no\s?commission\s?bac/i,
  roulette: /roulette|rulet|roleta|mini\s?roulette/i,
  blackjack: /black\s?jack|blackjack|\b21\b|twenty\s?one/i,
  poker: /poker|hold\s?em|holdem|texas|caribbean\s?stud|three\s?card/i,
  teenpatti: /teen\s?patti|teenpatti|3\s?patti|andar\s?bahar|andarbahar/i,
  table: /dragon\s?tiger|sic\s?bo|fan\s?tan|fantan|craps|super\s?six|casino\s?war|wheel\s?of/i,
  live: /\blive\b|live\s?casino|live\s?dealer|casino\s?live|casinolive/i,
  gameShow: /crazy\s?time|monopoly|deal\s?or\s?no|dream\s?catcher|mega\s?ball|game\s?show|cash\s?or\s?crash|adventures?\s?beyond|sweet\s?bonanza\s?candyland|lightning\s?(baccarat|roulette|dice|blackjack|ball)/i,
}

export const LIVE_CASINO_SEARCH_TERMS = [
  'baccarat',
  'roulette',
  'blackjack',
  'poker',
  'teen patti',
  'andar bahar',
  'dragon tiger',
  'sic bo',
  'live casino',
  'live dealer',
  'crazy time',
  'monopoly live',
  'game show',
  'craps',
  'casino holdem',
  'lightning roulette',
  'speed baccarat',
  'immersive roulette',
]

export const LIVE_CASINO_PROVIDERS = [
  'Evolution Live',
  'Ezugi',
  'Playtech',
  'KM',
  'JILI',
  'V8',
  '2J',
  'Mini',
  'Mac88',
  'Mac88 Gaming',
]

function getGameText(game: Record<string, unknown>): string {
  return [
    game.game_name,
    game.game_group,
    game.game_category,
    game.live_category,
    game.slot_category,
    game.provider,
    game.game_provider,
    game.provider_name,
  ]
    .filter(Boolean)
    .join(' ')
}

function getGameId(game: Record<string, unknown>): string {
  return String(game._id || game.game_identifier || game.identifier || '')
}

function isFalsePositiveSlot(game: Record<string, unknown>): boolean {
  const name = String(game.game_name || '')
  const group = String(game.game_group || game.game_category || '')
  if (!SLOT_GROUP_RE.test(group)) return false
  return FALSE_POSITIVE_NAMES.some((rx) => rx.test(name))
}

export function isLiveCasinoGame(game: Record<string, unknown>): boolean {
  if (isFalsePositiveSlot(game)) return false

  const text = getGameText(game)
  const group = String(game.game_group || game.game_category || '')
  const provider = String(game.game_provider || game.provider || game.provider_name || '')

  if (/casinolive|casino\s?live/i.test(group)) return true

  if (LIVE_PROVIDERS.has(provider)) {
    if (SLOT_GROUP_RE.test(group)) {
      return Object.entries(CATEGORY_PATTERNS).some(
        ([key, rx]) => key !== 'live' && rx.test(text)
      )
    }
    return true
  }

  if (/table|poker|card|roulette|multiplayer|india poker/i.test(group) && !SLOT_GROUP_RE.test(group)) {
    return Object.values(CATEGORY_PATTERNS).some((rx) => rx.test(text))
  }

  return Object.values(CATEGORY_PATTERNS).some((rx) => rx.test(text))
}

export function categorizeLiveGame(game: Record<string, unknown>): LiveCategoryId[] {
  const text = getGameText(game)
  const matched: LiveCategoryId[] = []

  for (const { id } of LIVE_CATEGORY_ORDER) {
    if (CATEGORY_PATTERNS[id].test(text)) matched.push(id)
  }

  if (matched.length === 0 && isLiveCasinoGame(game)) {
    matched.push('live')
  }

  return matched
}

export function groupGamesByCategory(games: Record<string, unknown>[]) {
  const groups = Object.fromEntries(
    LIVE_CATEGORY_ORDER.map(({ id }) => [id, [] as Record<string, unknown>[]])
  ) as Record<LiveCategoryId, Record<string, unknown>[]>

  for (const game of games) {
    if (!isLiveCasinoGame(game)) continue
    const categories = categorizeLiveGame(game)
    for (const cat of categories) {
      const list = groups[cat]
      if (!list.some((g) => getGameId(g) === getGameId(game))) {
        list.push(game)
      }
    }
  }

  return groups
}

export function mergeUniqueGames(
  arrays: Record<string, unknown>[][]
): Record<string, unknown>[] {
  const seen = new Set<string>()
  const merged: Record<string, unknown>[] = []

  for (const list of arrays) {
    for (const game of list) {
      const id = getGameId(game)
      if (!id || seen.has(id)) continue
      seen.add(id)
      merged.push(game)
    }
  }

  return merged
}

export function filterLiveGamesByQuery(
  games: Record<string, unknown>[],
  query: string
): Record<string, unknown>[] {
  const q = query.trim().toLowerCase()
  if (!q) return games
  return games.filter((game) => getGameText(game).toLowerCase().includes(q))
}
